import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import HeaderSection from '../../components/home/HeaderSection';
import FooterSection from '../../components/home/FooterSection';
import { ROUTES } from '../../constants/routes';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import { verifyPayment } from '../../services/payments';
import {
  clearPendingPayment,
  readPendingPayment,
  savePendingPayment,
} from '../../utils/pendingPayment';

const HOME_REDIRECT_SECONDS = 5;

function StatusIcon({ status }) {
  if (status === 'success') {
    return (
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#E8F5D8]">
        <svg className="h-10 w-10 text-[#69991D]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }

  if (status === 'verifying') {
    return (
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#E8F4FC]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1A87CC] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-50">
      <svg className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
  );
}

function resolveInitialStatus(statusProp, searchParams) {
  if (statusProp === 'success' || statusProp === 'failed') return statusProp;

  const linkStatus = (
    searchParams.get('razorpay_payment_link_status') ||
    searchParams.get('status') ||
    ''
  )
    .toLowerCase()
    .trim();

  if (linkStatus === 'paid' || linkStatus === 'success' || linkStatus === 'completed') {
    return 'success';
  }
  if (linkStatus && linkStatus !== 'created') return 'failed';

  if (
    searchParams.get('razorpay_payment_id') &&
    searchParams.get('razorpay_order_id') &&
    searchParams.get('razorpay_signature')
  ) {
    return 'verifying';
  }

  if (searchParams.get('razorpay_payment_id') || searchParams.get('subscription_id')) {
    return 'success';
  }

  return 'failed';
}

export default function PaymentResultPage({ status: statusProp }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetch } = useSubscriptionContext();
  const [secondsLeft, setSecondsLeft] = useState(HOME_REDIRECT_SECONDS);
  const [status, setStatus] = useState(() => resolveInitialStatus(statusProp, searchParams));
  const [errorMessage, setErrorMessage] = useState(
    () =>
      searchParams.get('error') ||
      searchParams.get('error_description') ||
      readPendingPayment()?.errorMessage ||
      'Your payment could not be completed. Please try again.'
  );

  const pending = useMemo(() => {
    const stored = readPendingPayment() || {};
    return {
      ...stored,
      paymentId: searchParams.get('razorpay_payment_id') || stored.paymentId || null,
      orderId: searchParams.get('razorpay_order_id') || stored.orderId || null,
      subscriptionId:
        searchParams.get('subscription_id') || stored.subscriptionId || null,
    };
  }, [searchParams]);

  const isSuccess = status === 'success';
  const isVerifying = status === 'verifying';

  // Call /api/payments/verify when all Razorpay fields are present.
  useEffect(() => {
    const razorpayPaymentId = searchParams.get('razorpay_payment_id');
    const razorpayOrderId = searchParams.get('razorpay_order_id');
    const razorpaySignature = searchParams.get('razorpay_signature');
    const subscriptionId =
      Number(searchParams.get('subscription_id')) ||
      Number(readPendingPayment()?.subscriptionId) ||
      null;

    if (!razorpayPaymentId || !razorpayOrderId || !razorpaySignature || !subscriptionId) {
      return undefined;
    }

    let cancelled = false;
    setStatus('verifying');

    (async () => {
      try {
        const res = await verifyPayment({
          subscriptionId,
          razorpayOrderId,
          razorpayPaymentId,
          razorpaySignature,
        });

        if (cancelled) return;

        if (res?.success === false) {
          const message = res?.message || 'Payment verification failed';
          savePendingPayment({
            ...(readPendingPayment() || {}),
            paymentId: razorpayPaymentId,
            orderId: razorpayOrderId,
            subscriptionId,
            errorMessage: message,
          });
          setErrorMessage(message);
          setStatus('failed');
          return;
        }

        savePendingPayment({
          ...(readPendingPayment() || {}),
          paymentId: razorpayPaymentId,
          orderId: razorpayOrderId,
          subscriptionId,
        });
        await refetch?.();
        setStatus('success');
      } catch (error) {
        if (cancelled) return;
        const message = error?.message || 'Payment verification failed';
        setErrorMessage(message);
        setStatus('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refetch, searchParams]);

  useEffect(() => {
    if (!isSuccess) return undefined;

    refetch?.();
    clearPendingPayment();

    const tick = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    const redirect = window.setTimeout(() => {
      navigate(ROUTES.HOME, { replace: true });
    }, HOME_REDIRECT_SECONDS * 1000);

    return () => {
      window.clearInterval(tick);
      window.clearTimeout(redirect);
    };
  }, [isSuccess, navigate, refetch]);

  useEffect(() => {
    if (isSuccess || isVerifying) return;
    clearPendingPayment();
  }, [isSuccess, isVerifying]);

  return (
    <>
      <HeaderSection />
      <main className="bg-[#F5F8FC] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-lg rounded-2xl bg-white px-6 py-10 text-center shadow-[0_8px_40px_rgba(0,0,0,0.08)] sm:px-10">
          <StatusIcon status={isVerifying ? 'verifying' : isSuccess ? 'success' : 'failed'} />

          <h1 className="mt-6 text-2xl font-bold text-[#1B1B1B] sm:text-[28px]">
            {isVerifying
              ? 'Verifying Payment'
              : isSuccess
                ? 'Payment Successful'
                : 'Payment Failed'}
          </h1>

          <p className="mt-3 text-sm leading-relaxed text-[#666666] sm:text-base">
            {isVerifying
              ? 'Please wait while we confirm your subscription…'
              : isSuccess
                ? 'Your subscription is active. You can start learning right away.'
                : errorMessage}
          </p>

          {(pending?.planName || pending?.amount || pending?.paymentId) && !isVerifying ? (
            <div className="mt-6 rounded-xl bg-[#F5F8FC] px-4 py-3 text-left text-sm text-[#1B1B1B]">
              {pending.planName ? (
                <p>
                  <span className="text-[#999999]">Plan: </span>
                  {pending.planName}
                </p>
              ) : null}
              {pending.amount != null ? (
                <p className="mt-1">
                  <span className="text-[#999999]">Amount: </span>
                  ₹{pending.amount}
                </p>
              ) : null}
              {pending.paymentId ? (
                <p className="mt-1 break-all">
                  <span className="text-[#999999]">Payment ID: </span>
                  {pending.paymentId}
                </p>
              ) : null}
            </div>
          ) : null}

          {isSuccess ? (
            <p className="mt-5 text-sm text-[#999999]">
              Redirecting to home in {secondsLeft}s…
            </p>
          ) : null}

          {!isVerifying ? (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              {isSuccess ? (
                <button
                  type="button"
                  onClick={() => navigate(ROUTES.HOME, { replace: true })}
                  className="rounded-lg bg-[#1A87CC] px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  Go to Home
                </button>
              ) : (
                <>
                  <Link
                    to={ROUTES.PRICING}
                    className="rounded-lg bg-[#1A87CC] px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                  >
                    Try Again
                  </Link>
                  <Link
                    to={ROUTES.HOME}
                    className="rounded-lg border border-[#1A87CC] px-6 py-3 text-sm font-semibold text-[#1A87CC] transition hover:bg-[#1A87CC]/5"
                  >
                    Go to Home
                  </Link>
                </>
              )}
            </div>
          ) : null}
        </div>
      </main>
      <FooterSection />
    </>
  );
}
